import {
  SupportTicketSection,
  SupportTicketState,
  SupportTicketStatus,
  SupportTicketSummary,
  SupportTicketThread,
  supportTicketApi,
} from '@/api/queries/supportTicket'
import { selectProfile } from '@store/profile.reducer'
import websocket from '@/utils/websocket'
import PlatformSupportLauncher, { PlatformSupportContext } from './PlatformSupportLauncher'
import classNames from 'classnames'
import { FC, FormEvent, Fragment, KeyboardEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import classes from './index.module.css'

type View =
  | { name: 'home' }
  | { name: 'compose'; section: SupportTicketSection }
  | { name: 'thread'; id: number }

interface Props {
  className?: string
  children?: ReactNode
  enabled?: boolean
}

const ADMIN_ROLES = ['admin', 'junior-admin']
const MAX_MESSAGE = 5000
const MAX_IMAGES = 5
const MAX_IMAGE_BYTES = 10 * 1024 * 1024
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']

type Draft = { file: File; preview: string }

const statusLabels: Record<SupportTicketStatus, string> = {
  open: 'Ждёт ответа',
  answered: 'Есть ответ',
  closed: 'Закрыто',
}

const formatTime = (timestamp: number) =>
  new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  }).format(new Date(timestamp * 1000))

/** Interceptor resolves 4xx with the axios error instead of rejecting: read both shapes. */
const unwrap = <T,>(response: any): T => {
  if (response?.data?.status === 'success') return response.data.data as T
  const error = response?.response?.data?.error ?? response?.data?.error
  throw new Error(Array.isArray(error) ? String(error[0] ?? '') : typeof error === 'string' ? error : '')
}

const icon = (paths: ReactNode) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {paths}
  </svg>
)

const sectionIcon = (name: string) => {
  const lower = name.toLowerCase()
  if (lower.includes('финанс') || lower.includes('оплат') || lower.includes('счёт') || lower.includes('счет')) {
    return icon(<><rect x="3" y="6" width="18" height="13" rx="2.5" /><path d="M3 10h18M16 15h2" /></>)
  }
  if (lower.includes('доработ') || lower.includes('идея') || lower.includes('предлож')) {
    return icon(<><path d="M9 18h6M10 21h4" /><path d="M12 3a6 6 0 0 0-3.6 10.8c.6.5 1 1.2 1.1 2h5c.1-.8.5-1.5 1.1-2A6 6 0 0 0 12 3Z" /></>)
  }
  return icon(<><circle cx="12" cy="12" r="9" /><path d="M9.6 9.3a2.5 2.5 0 1 1 3.4 2.3c-.6.3-1 .8-1 1.5v.4M12 16.8h.01" /></>)
}

const PlatformSupport: FC<Props> = ({ className, children, enabled = true }) => {
  const profile = useSelector(selectProfile) as { id?: number | string; role?: string; roles?: string[] } | undefined
  const isAdmin = enabled && Boolean(
    ADMIN_ROLES.includes(profile?.role ?? '') ||
    profile?.roles?.some?.((role) => ADMIN_ROLES.includes(role)),
  )
  const [state, setState] = useState<SupportTicketState | null>(null)
  const [open, setOpen] = useState(false)
  const [view, setView] = useState<View>({ name: 'home' })
  const [tickets, setTickets] = useState<SupportTicketSummary[] | null>(null)
  const [thread, setThread] = useState<SupportTicketThread | null>(null)
  const [draft, setDraft] = useState('')
  const [images, setImages] = useState<Draft[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [sending, setSending] = useState(false)
  const [escalating, setEscalating] = useState(false)
  const [error, setError] = useState('')
  const refreshStateRef = useRef<() => void>(() => {})
  const messagesRef = useRef<HTMLDivElement>(null)
  const viewRef = useRef(view)
  viewRef.current = view
  const openRef = useRef(open)
  openRef.current = open

  // Ответы поддержки приходят по вебсокету (platform_support.updated); опрос — только страховка
  // на случай обрыва сокета: раз в минуту без сокета, раз в 5 минут с ним, и при возврате на вкладку
  useEffect(() => {
    setState(null)
    if (!isAdmin || !profile?.id) return undefined
    let disposed = false
    let loading = false
    let lastCheck = 0
    const check = async (force = false) => {
      if (disposed || loading || document.hidden || (!force && Date.now() - lastCheck < 10000)) return
      loading = true
      lastCheck = Date.now()
      try {
        const next = unwrap<SupportTicketState>(await supportTicketApi.state())
        if (!disposed) setState(next)
      } catch {
        // Следующая попытка через минуту: сбой не должен мешать работе платформы
      } finally {
        loading = false
      }
    }
    refreshStateRef.current = () => { void check(true) }
    void check()
    const onActivity = () => { void check() }
    const interval = window.setInterval(() => {
      if (!websocket.isOpen() || Date.now() - lastCheck >= 300000) void check()
    }, 60000)
    // После переподключения сокета подтягиваем то, что могли пропустить
    const unsubscribeOpen = websocket.subscribeOpen(() => { void check(true) })
    window.addEventListener('focus', onActivity)
    document.addEventListener('visibilitychange', onActivity)
    return () => {
      disposed = true
      refreshStateRef.current = () => {}
      unsubscribeOpen?.()
      window.clearInterval(interval)
      window.removeEventListener('focus', onActivity)
      document.removeEventListener('visibilitychange', onActivity)
    }
  }, [isAdmin, profile?.id])

  const available = Boolean(isAdmin && state?.available)

  useEffect(() => {
    if (!available) setOpen(false)
  }, [available])

  const loadTickets = useCallback(async () => {
    try {
      setTickets(unwrap<SupportTicketSummary[]>(await supportTicketApi.list()))
    } catch {
      setTickets((current) => current ?? [])
    }
  }, [])

  const loadThread = useCallback(async (id: number) => {
    try {
      const next = unwrap<SupportTicketThread>(await supportTicketApi.view(id))
      const current = viewRef.current
      if (current.name === 'thread' && current.id === id) {
        setThread(next)
        refreshStateRef.current()
      }
    } catch (loadError) {
      const current = viewRef.current
      if (current.name === 'thread' && current.id === id) {
        setError((loadError as Error).message || 'Не удалось загрузить обращение. Проверьте соединение.')
      }
    }
  }, [])

  // Ответ поддержки или смена статуса: сервер шлёт событие только этому администратору
  useEffect(() => {
    if (!isAdmin || !profile?.id) return undefined
    const unsubscribe = websocket.subscribe('platform_support.updated', (message: { data?: { ticket_id?: number } }) => {
      refreshStateRef.current()
      if (!openRef.current) return
      const current = viewRef.current
      const ticketId = Number(message?.data?.ticket_id)
      if (current.name === 'thread' && current.id === ticketId) void loadThread(ticketId)
      else if (current.name === 'home') void loadTickets()
    })
    return () => { unsubscribe?.() }
  }, [isAdmin, profile?.id, loadThread, loadTickets])

  // При открытии загружаем данные; дальше обновляет сокет, а редкий опрос страхует его обрыв
  useEffect(() => {
    if (!open || view.name === 'compose') return undefined
    const refresh = () => {
      if (document.hidden) return
      if (view.name === 'thread') void loadThread(view.id)
      else void loadTickets()
    }
    refresh()
    const interval = window.setInterval(() => {
      if (!websocket.isOpen()) refresh()
    }, view.name === 'thread' ? 15000 : 30000)
    return () => window.clearInterval(interval)
  }, [open, view, loadThread, loadTickets])

  useEffect(() => {
    const list = messagesRef.current
    if (list) list.scrollTop = list.scrollHeight
  }, [thread?.id, thread?.messages.length])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  const clearImages = () => {
    setImages((current) => {
      current.forEach((image) => URL.revokeObjectURL(image.preview))
      return []
    })
  }

  // Скрепка, вставка из буфера (Ctrl+V скриншота) и перетаскивание ведут сюда
  const addImages = (list: FileList | File[] | null) => {
    const files = Array.from(list ?? [])
    if (!files.length) return
    const accepted = files.filter((file) => IMAGE_TYPES.includes(file.type) && file.size <= MAX_IMAGE_BYTES)
    const room = MAX_IMAGES - images.length
    const added = accepted.slice(0, Math.max(0, room))
    if (accepted.length < files.length) {
      setError('Можно прикладывать изображения JPG, PNG, WEBP и GIF до 10 МБ.')
    } else if (added.length < accepted.length) {
      setError(`Можно приложить не больше ${MAX_IMAGES} изображений.`)
    } else {
      setError('')
    }
    if (added.length) {
      setImages((current) => [...current, ...added.map((file) => ({ file, preview: URL.createObjectURL(file) }))])
    }
  }

  const removeImage = (index: number) => {
    setImages((current) => {
      URL.revokeObjectURL(current[index].preview)
      return current.filter((_, position) => position !== index)
    })
  }

  useEffect(() => () => {
    setImages((current) => {
      current.forEach((image) => URL.revokeObjectURL(image.preview))
      return current
    })
  }, [])

  const go = (next: View) => {
    setView(next)
    setDraft('')
    clearImages()
    setError('')
    if (next.name !== 'thread') setThread(null)
  }

  const submit = async (event?: FormEvent) => {
    event?.preventDefault()
    const message = draft.trim()
    if ((!message && !images.length) || sending || view.name === 'home') return
    setSending(true)
    setError('')
    const files = images.map((image) => image.file)
    try {
      const response = view.name === 'compose'
        ? await supportTicketApi.create(view.section.id, message, files)
        : await supportTicketApi.reply(view.id, message, files)
      const next = unwrap<SupportTicketThread>(response)
      setDraft('')
      clearImages()
      setThread(next)
      setView({ name: 'thread', id: next.id })
      void loadTickets()
    } catch (submitError) {
      setError((submitError as Error).message || 'Не удалось отправить сообщение. Проверьте соединение и попробуйте ещё раз.')
    } finally {
      setSending(false)
    }
  }

  // «Общение со специалистом»: ИИ-ассистент больше не отвечает, обращение уходит команде
  const escalate = async () => {
    if (!thread || escalating) return
    setEscalating(true)
    setError('')
    try {
      setThread(unwrap<SupportTicketThread>(await supportTicketApi.escalate(thread.id)))
      void loadTickets()
    } catch (escalateError) {
      setError((escalateError as Error).message || 'Не удалось позвать специалиста. Попробуйте ещё раз.')
    } finally {
      setEscalating(false)
    }
  }

  const onDraftKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault()
      void submit()
    }
  }

  if (!available || !state) {
    // Keep the page mounted while support availability is loading or changing.
    return (
      <PlatformSupportContext.Provider value={null}>
        <Fragment key="content">{children}</Fragment>
      </PlatformSupportContext.Provider>
    )
  }

  const title = view.name === 'compose' ? view.section.name
    : view.name === 'thread' ? (thread ? `Обращение #${thread.id}` : 'Обращение') : 'Техподдержка'

  const composer = (placeholder: string, button: string) => (
    <form
      className={classes.composer}
      onSubmit={submit}
      onDragOver={(event) => { if (event.dataTransfer?.types?.includes('Files')) event.preventDefault() }}
      onDrop={(event) => {
        if (!event.dataTransfer?.files?.length) return
        event.preventDefault()
        addImages(event.dataTransfer.files)
      }}
    >
      <textarea
        className={classes.textarea}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={onDraftKeyDown}
        onPaste={(event) => {
          const pasted = Array.from(event.clipboardData?.files ?? []).filter((file) => file.type.startsWith('image/'))
          if (!pasted.length) return
          event.preventDefault()
          addImages(pasted)
        }}
        maxLength={MAX_MESSAGE}
        rows={view.name === 'compose' ? 6 : 3}
        placeholder={placeholder}
        aria-label={placeholder}
        disabled={sending}
        autoFocus
      />
      {images.length > 0 && (
        <ul className={classes.previews} aria-label="Приложенные изображения">
          {images.map((image, index) => (
            <li key={image.preview}>
              <img src={image.preview} alt={image.file.name} />
              <button type="button" onClick={() => removeImage(index)} aria-label={`Убрать ${image.file.name}`} disabled={sending}>×</button>
            </li>
          ))}
        </ul>
      )}
      {error && <p className={classes.error} role="alert">{error}</p>}
      <div className={classes.composerFooter}>
        <input
          ref={fileInputRef}
          type="file"
          accept={IMAGE_TYPES.join(',')}
          multiple
          hidden
          onChange={(event) => {
            addImages(event.target.files)
            event.target.value = ''
          }}
        />
        <button
          type="button"
          className={classes.attach}
          onClick={() => fileInputRef.current?.click()}
          disabled={sending || images.length >= MAX_IMAGES}
          aria-label="Приложить изображение"
          title="Приложить изображение (можно вставить скриншот через Ctrl + V)"
        >
          {icon(<path d="m21 11.5-8.6 8.6a5.5 5.5 0 0 1-7.8-7.8l8.6-8.6a3.7 3.7 0 0 1 5.2 5.2l-8.6 8.6a1.8 1.8 0 0 1-2.6-2.6l8-8" />)}
        </button>
        <span className={classes.hint}>Ctrl + Enter — отправить</span>
        <button type="submit" className={classes.send} disabled={sending || (!draft.trim() && !images.length)}>
          {sending ? 'Отправляем…' : button}
        </button>
      </div>
    </form>
  )

  const panel = (
    <section className={classes.panel} role="dialog" aria-label="Техподдержка платформы">
      <header className={classes.header}>
        {view.name !== 'home' && (
          <button type="button" className={classes.iconButton} onClick={() => go({ name: 'home' })} aria-label="Назад">
            {icon(<path d="m15 18-6-6 6-6" />)}
          </button>
        )}
        <div className={classes.headerText}>
          <h2>{title}</h2>
          <span>
            {view.name === 'thread' && thread
              ? `${thread.section} · ${statusLabels[thread.status]}`
              : 'Команда Импульс ЛМС ответит здесь'}
          </span>
        </div>
        <button type="button" className={classes.iconButton} onClick={() => setOpen(false)} aria-label="Закрыть">
          {icon(<path d="M6 6l12 12M18 6 6 18" />)}
        </button>
      </header>

      {view.name === 'home' && (
        <div className={classes.body}>
          <p className={classes.lead}>Выберите раздел, и мы ответим вам здесь.</p>
          {state.sections.length > 0 ? (
            <div className={classes.sections}>
              {state.sections.map((section) => (
                <button key={section.id} type="button" className={classes.section} onClick={() => go({ name: 'compose', section })}>
                  <span className={classes.sectionIcon}>{sectionIcon(section.name)}</span>
                  <span className={classes.sectionName}>{section.name}</span>
                  <span className={classes.chevron}>{icon(<path d="m9 6 6 6-6 6" />)}</span>
                </button>
              ))}
            </div>
          ) : (
            <p className={classes.empty}>Разделы обращений пока не настроены.</p>
          )}
          {tickets && tickets.length > 0 && (
            <>
              <h3 className={classes.subtitle}>Мои обращения</h3>
              <ul className={classes.tickets}>
                {tickets.map((ticket) => (
                  <li key={ticket.id}>
                    <button type="button" className={classes.ticket} onClick={() => go({ name: 'thread', id: ticket.id })}>
                      <span className={classes.ticketTop}>
                        <b>#{ticket.id} · {ticket.section}</b>
                        <span className={classNames(classes.status, classes[`status_${ticket.status}`])}>{statusLabels[ticket.status]}</span>
                      </span>
                      <span className={classes.ticketPreview}>
                        {ticket.unread && <i className={classes.unreadDot} aria-label="Новый ответ" />}
                        {ticket.lastMessage.fromSupport ? 'Поддержка: ' : 'Вы: '}{ticket.lastMessage.body || '📎 Изображение'}
                      </span>
                      <time className={classes.ticketTime}>{formatTime(ticket.updatedAt)}</time>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {view.name === 'compose' && (
        <div className={classes.body}>
          <p className={classes.lead}>Опишите вопрос подробно: что делали, что ожидали и что получилось. Ответ придёт в это окно.</p>
          {view.section.ai && (
            <p className={classes.aiLead}>🤖 Сначала ответит ИИ-ассистент. Если он не поможет — в переписке будет кнопка «Общение со специалистом».</p>
          )}
          {composer('Ваше сообщение…', 'Отправить')}
        </div>
      )}

      {view.name === 'thread' && (
        <>
          <div ref={messagesRef} className={classes.messages} aria-live="polite">
            {!thread && !error && <p className={classes.empty}>Загружаем…</p>}
            {thread?.messages.map((message) => (
              <article key={message.id} className={classNames(classes.message, message.fromSupport ? classes.fromSupport : classes.fromMe)}>
                <header>
                  <b>{!message.fromSupport ? 'Вы' : message.fromAi ? '🤖 ИИ-ассистент' : 'Техподдержка'}</b>
                  {message.fromSupport && !message.fromAi && message.authorName && <span>{message.authorName}</span>}
                  <time>{formatTime(message.createdAt)}</time>
                </header>
                {message.body && <div className={classes.messageBody}>{message.body}</div>}
                {message.attachments && message.attachments.length > 0 && (
                  <div className={classes.files}>
                    {message.attachments.map((file) => (
                      <a key={file.url} href={file.url} target="_blank" rel="noopener noreferrer" title={file.name}>
                        <img src={file.url} alt={file.name} loading="lazy" />
                      </a>
                    ))}
                  </div>
                )}
              </article>
            ))}
          </div>
          {thread?.handler === 'ai' && (
            <div className={classes.aiNote}>
              <span>🤖 Сейчас отвечает ИИ-ассистент</span>
              <button type="button" className={classes.specialist} onClick={() => { void escalate() }} disabled={escalating}>
                {escalating ? 'Зовём специалиста…' : 'Общение со специалистом'}
              </button>
            </div>
          )}
          {thread?.handler === 'human' && thread.escalated && thread.status !== 'closed' && (
            <p className={classes.closedNote}>🙋 Вопрос передан специалисту — он ответит здесь.</p>
          )}
          {thread?.status === 'closed' && (
            <p className={classes.closedNote}>Обращение закрыто. Если вопрос остался — напишите, и мы откроем его снова.</p>
          )}
          {composer('Написать сообщение…', 'Отправить')}
        </>
      )}
    </section>
  )

  const toggle = () => {
    if (!open) go({ name: 'home' })
    setOpen((value) => !value)
  }

  return (
    <PlatformSupportContext.Provider value={{ open, unread: state.unread, toggle }}>
      <Fragment key="content">{children === undefined ? <PlatformSupportLauncher className={className} /> : children}</Fragment>
      {open && createPortal(panel, document.body)}
    </PlatformSupportContext.Provider>
  )
}

export default PlatformSupport
